import { PlusOutlined } from '@ant-design/icons'
import type { ViewFieldsFragment } from '@salescore/client-api'
import { checkSyncEnabled, checkVisualizeEnabled, type Me, useMe } from '@salescore/client-common'
import { openThread, useAppDispatch } from '@salescore/client-redux'
import type { CompiledSheetViewConfig, EltChangeResult, ViewQueryRecordNode } from '@salescore/core'
// eslint-disable-next-line sonarjs/deprecation
import { useModal } from '@salescore/frontend-common'
import { Alert, Button, Empty, Modal } from 'antd'
import { Match } from 'effect'
import { type ReactElement, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import type { SheetColumn, SheetSystemColumn } from '../../domain/column.ts'
import { useSharedUrl } from '../../hooks/useSharedUrl.ts'
import { useSheetSystemColumns } from '../../hooks/useSheetSystemColumns.ts'
import type { SheetTableContext } from '../../recoil/context'
import { useSheetColumns } from '../../recoil/selectors/useSheetColumns.tsx'
import type { SalescoreObjectAbility, ViewAbility } from '../../recoil/view/atoms'
import { useAsyncHandleKeyDown } from '../../rsheet/recoil/mutations/useAsyncHandleKeyDown.ts'
import { ViewUISheetBody } from './ViewUISheet'
import { PropertySelectorTableForContextMenu } from './ViewUISheet/PropertySelectorTableForContextMenu.tsx'
import { SheetNavigation } from './ViewUISheetWithNavigation/SheetNavigation'
import { SheetSelectionBar } from './ViewUISheetWithNavigation/SheetSelectionBar'

const canShowSheetComponent = (me: Me, isGoalView: boolean | undefined): boolean => {
  const isSyncEnabled = checkSyncEnabled(me.myUser)
  const isVisualizeEnabled = checkVisualizeEnabled(me.myUser)

  // sync,visライセンスがある場合、または目標設定からのドリルダウン時に閲覧可能とする
  // cf. https://intercom.help/salescore/ja/articles/9937605-%E3%83%A9%E3%82%A4%E3%82%BB%E3%83%B3%E3%82%B9%E3%81%A8%E6%A8%A9%E9%99%90%E3%81%AB%E3%81%A4%E3%81%84%E3%81%A6
  return isSyncEnabled || isVisualizeEnabled || isGoalView === true
}

export const ViewUISheetWithNavigator = ({
  compiledConfig,
  view,
  setView,
  ability,
  hasChangeToViewSchema,
  setHasChangeToViewSchema,
}: {
  compiledConfig: CompiledSheetViewConfig
  view: ViewFieldsFragment
  setView: (f: (v: ViewFieldsFragment) => ViewFieldsFragment) => void
  ability: ViewAbility & SalescoreObjectAbility
  hasChangeToViewSchema: boolean
  setHasChangeToViewSchema: (hasChangeToViewSchema: boolean) => void
}): ReactElement => {
  const [component] = compiledConfig.ui
  const [recordSaveErrors, setRecordSaveErrors] = useState<EltChangeResult[]>([])
  const me = useMe()
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const sharedUrl = useSharedUrl()

  // eslint-disable-next-line sonarjs/deprecation
  const initializeModal = useModal()

  const context = useMemo(
    (): SheetTableContext => ({
      type: 'sheet',
      fixedLeftColumnIndex: component.fixedLeftColumnIndex ?? 0,
      isGoalView: component.isGoalView ?? false,
    }),
    [component.fixedLeftColumnIndex, component.isGoalView],
  )

  const query = compiledConfig.queries[0]
  const sheetColumns = useSheetColumns(compiledConfig, query)
  const systemColumns = useSheetSystemColumns(query)
  const visibleFieldColumns = useMemo(
    () => sheetColumns.filter((x) => x.visible === undefined || x.visible),
    [sheetColumns],
  )

  const columns: Array<SheetSystemColumn<ViewQueryRecordNode> | SheetColumn<ViewQueryRecordNode>> = useMemo(
    () =>
      [...systemColumns, ...visibleFieldColumns].map((x, index) => ({
        ...x,
        index,
      })),
    [visibleFieldColumns, systemColumns],
  )

  const asyncHandleKeyDown = useAsyncHandleKeyDown(
    view.id,
    columns,
    setRecordSaveErrors,
    compiledConfig,
    view,
    ability,
    query,
  )

  useEffect(() => {
    const thread = sharedUrl?.sheetThreadComment?.sheetThread
    if (thread === undefined) {
      return
    }
    dispatch(
      openThread({
        viewId: view.id,
        recordId: thread.recordId,
        threadId: thread.id,
      }),
    )
  }, [])

  return (
    <div
      // relative は SheetSelectionBar (getContainer={false} の Drawer) の絶対配置基準
      className="relative flex flex-col overflow-y-auto bg-white"
      style={{
        height: '100%',
        WebkitBorderBottomLeftRadius: 10,
        WebkitBorderBottomRightRadius: 10,
        WebkitBorderTopRightRadius: 10,
      }}
    >
      {canShowSheetComponent(me, context.isGoalView) ? (
        <>
          <SheetNavigation
            component={component}
            recordSaveErrors={recordSaveErrors}
            setRecordSaveErrors={setRecordSaveErrors}
            columns={columns}
            sheetColumns={sheetColumns}
            compiledConfig={compiledConfig}
            view={view}
            setView={setView}
            ability={ability}
            query={query}
            hasChangeToViewSchema={hasChangeToViewSchema}
            setHasChangeToViewSchema={setHasChangeToViewSchema}
          />

          <div
            className="h-full min-h-0 flex-1 border"
            // ここでkey指定するとviewの切り替え時に全レンダリングが走って良くないが、実装ミスでレンダリングが微妙になるのを防ぐのを優先する
            key={view.id}
            onKeyDown={(event) => {
              void asyncHandleKeyDown(event)
            }}
          >
            {Match.value({ component, sheetColumns, visibleFieldColumns }).pipe(
              Match.when(
                ({ component }) => component.columns.length === 0,
                (_) => (
                  <div className="pt-8">
                    <Empty
                      description={
                        <div>
                          <p>{t(`カラムが設定されていません。`)}</p>
                          <br />
                          <Button onClick={initializeModal.showModal} icon={<PlusOutlined />} type="primary">
                            {t(`カラムを追加`)}
                          </Button>
                        </div>
                      }
                    />
                  </div>
                ),
              ),
              Match.when(
                ({ sheetColumns }) => sheetColumns.length === 0,
                (_) => (
                  <div className="pt-8">
                    <Empty
                      description={
                        <div>
                          <p>{t(`カラムが設定されていない、もしくは全てのフィールドが連携先から削除されました。`)}</p>
                          <br />
                          <Button onClick={initializeModal.showModal} icon={<PlusOutlined />} type="primary">
                            {t(`カラムを追加`)}
                          </Button>
                        </div>
                      }
                    />
                  </div>
                ),
              ),
              Match.when(
                ({ visibleFieldColumns }) => visibleFieldColumns.length === 0,
                (_) => (
                  <div className="pt-8">
                    <Empty
                      description={
                        <div>
                          <p>{t(`全てのカラムが非表示になっています。`)}</p>
                        </div>
                      }
                    />
                  </div>
                ),
              ),
              Match.orElse((): ReactElement => (
                <ViewUISheetBody
                  context={context}
                  columns={columns}
                  compiledConfig={compiledConfig}
                  view={view}
                  ability={ability}
                  query={query}
                  setHasChangeToViewSchema={setHasChangeToViewSchema}
                />
              )),
            )}
            <div
              onKeyDown={(event) => {
                // シートのイベントが発火しない様にイベントの伝播を止める
                event.stopPropagation()
              }}
            >
              <Modal
                open={initializeModal.isModalVisible}
                onCancel={initializeModal.hideModal}
                width={'60%'}
                cancelText={t(`閉じる`)}
                okButtonProps={{ style: { display: 'none' } }}
                title={<div></div>}
                style={{ top: '3%' }}
                destroyOnHidden
              >
                <PropertySelectorTableForContextMenu
                  initialNodePath={[]}
                  configIndex={-1} // configIndex の先頭は -1 で表現することになっている
                  columns={columns}
                  compiledConfig={compiledConfig}
                  view={view}
                  query={query}
                  setHasChangeToViewSchema={setHasChangeToViewSchema}
                />
              </Modal>
            </div>
          </div>
        </>
      ) : (
        <Alert
          type="warning"
          message={t(`SYNCもしくはVISUALIZEライセンスが有効化されていないため、シートを閲覧できません`)}
        />
      )}
      <SheetSelectionBar compiledConfig={compiledConfig} view={view} ability={ability} query={query} />
    </div>
  )
}
